import classNames from "classnames";
import * as React from "react";
import { StyleClass } from "source/app/presentation/Shared/Styles/CSS";
import { BookOfMindQuote } from "motw_core/DTOs/BookOfMindQuote";
import { DisplayQuoteParent } from "../DisplayQuoteParent/DisplayQuoteParent";
import { ArrayUtils } from "source/app/services/ArrayUtils";
import { Sounds } from "source/app/services/Sound/Sounds";
import * as styles from "./DisplayBookOfMindQuote.css";

export function DisplayQuote({
    bomq,
    index,
    wordRevealOffset,
    spokenQuoteIndex,
    spokenWordStartIndex,
    spokenWordEndIndex,
    authorName,
    parentCollectionName,
}: {
    bomq: BookOfMindQuote;
    index: number;
    wordRevealOffset?: number;
    spokenQuoteIndex?: number;
    spokenWordStartIndex?: number;
    spokenWordEndIndex?: number;
    authorName?: string;
    parentCollectionName?: string;
}) {
    const isBeingSpoken = spokenQuoteIndex === index;
    const hasActiveWord =
        isBeingSpoken &&
        spokenWordStartIndex !== undefined &&
        spokenWordEndIndex !== undefined;
    const activeWordRange = hasActiveWord
        ? {
              startIndex: spokenWordStartIndex!,
              endIndex: spokenWordEndIndex!,
          }
        : undefined;

    const handleMouseEnter = () => {
        const sound = GetQuoteHoverSound();
        // console.log(sound.filename)
        sound.Play({ volume: 0.3, allowConcurrent: true });
    };

    return (
        <span>
            <span
                className={classNames({
                    [nonSpokenStyleClass]: !isBeingSpoken,
                    [spokenStyleClass]: isBeingSpoken,
                })}
                style={{
                    backgroundColor: isBeingSpoken
                        ? `#8b6507`
                        : `transparent`,
                    textShadow: isBeingSpoken
                        ? `0px 0px 10px gold`
                        : `none`,
                    transition:
                        "background-color 500ms ease, text-shadow 500ms ease",
                }}
                onMouseEnter={handleMouseEnter}
            >
                <SpokenQuoteText
                    text={bomq.quote}
                    activeWordRange={activeWordRange}
                    wordRevealOffset={wordRevealOffset}
                />
            </span>
            <DisplayQuoteParent
                bomq={bomq}
                authorName={authorName}
                parentCollectionName={parentCollectionName}
                revealDelayMs={GetQuoteRevealEndDelay(
                    GetWordRanges(bomq.quote).length,
                    wordRevealOffset ?? 0
                )}
            />
        </span>
    );
}

export function GetQuoteRevealEndDelay(
    wordCount: number,
    wordRevealOffset: number
) {
    const lastWordIndex = Math.max(wordCount - 1, 0);
    return (
        wordRevealInitialDelayInMilliseconds +
        (wordRevealOffset + lastWordIndex) * wordRevealDelayInMilliseconds +
        wordRevealDurationInMilliseconds
    );
}

function SpokenQuoteText({
    text,
    activeWordRange,
    wordRevealOffset = 0,
}: {
    text: string;
    activeWordRange?: IWordRange;
    wordRevealOffset?: number;
}) {
    const previousWordRangeRef = React.useRef<IWordRange>();
    const [previousWordRange, setPreviousWordRange] =
        React.useState<IWordRange>();
    const [isTransitionVisible, setIsTransitionVisible] =
        React.useState(true);

    const activeWordStartIndex = activeWordRange?.startIndex;
    const activeWordEndIndex = activeWordRange?.endIndex;

    React.useLayoutEffect(() => {
        const previousRange = previousWordRangeRef.current;
        previousWordRangeRef.current = activeWordRange;

        if (!previousRange) {
            setPreviousWordRange(undefined);
            setIsTransitionVisible(true);
            return;
        }

        setPreviousWordRange(previousRange);
        setIsTransitionVisible(false);

        const animationFrame = window.requestAnimationFrame(() => {
            setIsTransitionVisible(true);
        });
        const timeoutId = window.setTimeout(() => {
            setPreviousWordRange(undefined);
        }, wordHighlightTransitionDurationInMilliseconds);

        return () => {
            window.cancelAnimationFrame(animationFrame);
            window.clearTimeout(timeoutId);
        };
    }, [activeWordStartIndex, activeWordEndIndex, text]);

    const wordRanges = GetWordRanges(text);
    let textPosition = 0;

    return (
        <>
            {wordRanges.map((wordRange, index) => {
                const leadingText = text.slice(
                    textPosition,
                    wordRange.startIndex
                );
                textPosition = wordRange.endIndex;

                const isCurrentWord = IsInWordRange(
                    wordRange.startIndex,
                    wordRange.endIndex,
                    activeWordRange
                );
                const isPreviousWord = IsInWordRange(
                    wordRange.startIndex,
                    wordRange.endIndex,
                    previousWordRange
                );

                return (
                    <React.Fragment key={wordRange.startIndex}>
                        {leadingText}
                        <span
                            className={`${styles.wordReveal} ${
                                isCurrentWord || isPreviousWord
                                    ? activeWordStyleClass
                                    : ""
                            }`}
                            style={{
                                animationDelay: `${
                                    wordRevealInitialDelayInMilliseconds +
                                    (wordRevealOffset + index) *
                                    wordRevealDelayInMilliseconds
                                }ms`,
                                ...(isCurrentWord || isPreviousWord
                                    ? {
                                          color: isCurrentWord
                                              ? `#fffdf2`
                                              : `#f4e6c1`,
                                          opacity: 1,
                                          backgroundColor:
                                              isCurrentWord ===
                                              isTransitionVisible
                                                  ? `#4b371d`
                                                  : `transparent`,
                                          textShadow:
                                              isCurrentWord ===
                                              isTransitionVisible
                                                  ? `0px 0px 4px gold`
                                                  : `none`,
                                          transition: `background-color ${
                                              isCurrentWord
                                                  ? wordHighlightFadeInDurationInMilliseconds
                                                  : wordHighlightFadeOutDurationInMilliseconds
                                          }ms ${
                                              isCurrentWord ? `ease` : `ease-out`
                                          }, text-shadow ${
                                              isCurrentWord
                                                  ? wordHighlightFadeInDurationInMilliseconds
                                                  : wordHighlightFadeOutDurationInMilliseconds
                                          }ms ${
                                              isCurrentWord ? `ease` : `ease-out`
                                          }`,
                                      }
                                    : {}),
                            }}
                        >
                            {text.slice(
                                wordRange.startIndex,
                                wordRange.endIndex
                            )}
                        </span>
                    </React.Fragment>
                );
            })}
            {text.slice(textPosition)}
        </>
    );
}

function IsInWordRange(
    startIndex: number,
    endIndex: number,
    wordRange?: IWordRange
) {
    return (
        wordRange !== undefined &&
        startIndex >= wordRange.startIndex &&
        endIndex <= wordRange.endIndex
    );
}

interface IWordRange {
    startIndex: number;
    endIndex: number;
}

function GetWordRanges(text: string): IWordRange[] {
    const wordRanges: IWordRange[] = [];
    const wordPattern = /\S+/g;
    let match: RegExpExecArray | null;

    while ((match = wordPattern.exec(text)) !== null) {
        wordRanges.push({
            startIndex: match.index,
            endIndex: match.index + match[0].length,
        });
    }

    return wordRanges;
}

function GetQuoteHoverSound() {
    return ArrayUtils.GetRandomElement([
        Sounds.harp_pluck_C4,
        Sounds.harp_pluck_C5,
        Sounds.harp_pluck_C6,
        Sounds.harp_pluck_C7,
    ]);
}

const spokenStyleClass = StyleClass(`displayBookOfMindSingleQuote_spoken`, {
    background: `#8b6507`,
    textShadow: `0px 0px 10px gold`,
    borderRadius: `4px`,
});

const nonSpokenStyleClass = StyleClass(
    `displayBookOfMindSingleQuote_nonSpoken`,
    {
        background: `inherit`,
        textShadow: `inherit`,
        padding: `inherit`,
        borderRadius: `4px`,
    }
);

const activeWordStyleClass = StyleClass(`displayBookOfMindActiveWord_bronze`, {
    background: `#4b371d`,
    color: `#f4e6c1`,
    textShadow: `0px 0px 4px gold`,
    padding: `0px 2px`,
    margin: `0px -2px`,
    borderRadius: `2px`,
});

const wordHighlightFadeInDurationInMilliseconds = 200;
const wordHighlightFadeOutDurationInMilliseconds = 400;
const wordHighlightTransitionDurationInMilliseconds =
    wordHighlightFadeOutDurationInMilliseconds;
export const wordRevealDelayInMilliseconds = 16;
export const wordRevealInitialDelayInMilliseconds = 500;
export const wordRevealDurationInMilliseconds = 420;
